// Active-filter banner for the Variation section. Adds Varies + Type to
// the SOW shape (Status / Date range / Include archived). The Varies chip
// stores `VariesToken` ids — the contract sentinel `'contract'` plus
// SOW ids — so the summary line resolves SOW ids to `{number} · {name}`
// labels by looking them up in the same SOW list the chip bar uses.

import type { Sow, VariationStatus } from "../../../../types";
import type {
  VariationDateRangeFilter,
  VariationFilters,
  VariationTypeFilter,
} from "./applyVariationFilters";
import { hasAnyActiveVariationFilter } from "./hasAnyActiveVariationFilter";

export interface VariationActiveFilterSummaryProps {
  filters: VariationFilters;
  search: string;
  sows: readonly Sow[];
  onClearAll: () => void;
}

const STATUS_LABEL: Record<VariationStatus, string> = {
  proposed: "Proposed",
  approved: "Approved",
  rejected: "Rejected",
};

const TYPE_LABEL: Record<VariationTypeFilter, string> = {
  financial: "Financial",
  "non-financial": "Non-financial",
};

const DATE_RANGE_LABEL: Record<VariationDateRangeFilter, string> = {
  "next-30-days": "Next 30 days",
  "past-30-days": "Past 30 days",
  "this-quarter": "This quarter",
  "all-time": "All time",
};

function joinLabels<T extends string>(
  values: T[],
  labels: Record<T, string>,
): string {
  return values.map((v) => labels[v]).join(", ");
}

function variesLabel(token: string, sows: readonly Sow[]): string {
  if (token === "contract") return "Contract";
  const s = sows.find((x) => x.id === token);
  return s ? `${s.number} · ${s.name}` : token;
}

export default function VariationActiveFilterSummary({
  filters,
  search,
  sows,
  onClearAll,
}: VariationActiveFilterSummaryProps) {
  if (!hasAnyActiveVariationFilter(filters, search)) return null;

  const parts: string[] = [];
  if (filters.status.length > 0) {
    parts.push(`Status: ${joinLabels(filters.status, STATUS_LABEL)}`);
  }
  if (filters.varies.length > 0) {
    parts.push(
      `Varies: ${filters.varies.map((t) => variesLabel(t, sows)).join(", ")}`,
    );
  }
  if (filters.type.length > 0) {
    parts.push(`Type: ${joinLabels(filters.type, TYPE_LABEL)}`);
  }
  if (filters.dateRange !== "all-time") {
    parts.push(`Date range: ${DATE_RANGE_LABEL[filters.dateRange]}`);
  }
  if (filters.includeArchived) {
    parts.push("Include archived");
  }
  const trimmedSearch = search.trim();
  if (trimmedSearch) {
    parts.push(`Search: '${trimmedSearch}'`);
  }

  return (
    <div
      data-testid="variation-active-filter-summary"
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-caption text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-300"
    >
      <span>{parts.join(" · ")}</span>
      <button
        type="button"
        onClick={onClearAll}
        data-testid="variation-active-filter-clear-all"
        className="text-caption font-medium text-brand-600 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-brand-400"
      >
        Clear all
      </button>
    </div>
  );
}
